import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ImageUploadField, PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input } from 'components/common';
import { createHomeBanner, getAdminHomeBannerById, updateHomeBanner } from 'apis/adminHomeBanners';
import { useActiveLanguagesQuery, useAdminCreateMutation, useAdminDetailQuery, useAdminUpdateMutation } from 'hooks';
import '../../../styles/admin-shared.scss';

const HomeBannersForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({
        image: '',
        imageEn: '',
        primaryButtonUrl: '',
        secondaryButtonUrl: '',
        sortOrder: 0,
        isActive: true,
        translation: [],
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: detail, error: detailError } = useAdminDetailQuery('home-banners', id, getAdminHomeBannerById);
    const createMutation = useAdminCreateMutation('home-banners', createHomeBanner);
    const updateMutation = useAdminUpdateMutation('home-banners', updateHomeBanner);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({
                ...prev,
                translation: languages.map(l => ({
                    languageId: l.id,
                    eyebrow: '',
                    title: '',
                    lead: '',
                    primaryButtonText: '',
                    secondaryButtonText: '',
                })),
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail) {
            setFormData({
                image: detail.image || '',
                imageEn: detail.imageEn || '',
                primaryButtonUrl: detail.primaryButtonUrl || '',
                secondaryButtonUrl: detail.secondaryButtonUrl || '',
                sortOrder: detail.sortOrder ?? 0,
                isActive: detail.isActive ?? true,
                translation: detail.translation || [],
            });
        }
    }, [detail]);

    useEffect(() => { if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại'); }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({
            ...prev,
            translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                image: formData.image,
                imageEn: formData.imageEn,
                primaryButtonUrl: formData.primaryButtonUrl || null,
                secondaryButtonUrl: formData.secondaryButtonUrl || null,
                sortOrder: Number(formData.sortOrder),
                isActive: formData.isActive,
                translation: languages.map(lang => {
                    const t = formData.translation.find(x => x.languageId === lang.id) || {};
                    return {
                        languageId: lang.id,
                        eyebrow: t.eyebrow || '',
                        title: t.title || '',
                        lead: t.lead || '',
                        primaryButtonText: t.primaryButtonText || '',
                        secondaryButtonText: t.secondaryButtonText || '',
                    };
                }),
            };
            const res = id ? await updateMutation.mutateAsync({ id, data: payload }) : await createMutation.mutateAsync(payload);
            if (res.success) {
                toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công');
                navigate('/admin/home-banners');
            } else {
                toast.error(res.message || 'Lưu thất bại');
            }
        } catch {
            toast.error('Lưu thất bại');
        }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Banner Trang chủ' : 'Thêm Banner Trang chủ mới'} />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt chung Banner</p>
                <div className="form-row">
                    <ImageUploadField
                        label="Hình ảnh Banner (Tiếng Việt/Mặc định)"
                        value={formData.image}
                        folder="home-banners"
                        onChange={(image) => setFormData(prev => ({ ...prev, image }))}
                    />
                    <ImageUploadField
                        label="Hình ảnh Banner (Tiếng Anh - Tùy chọn)"
                        value={formData.imageEn}
                        folder="home-banners"
                        onChange={(imageEn) => setFormData(prev => ({ ...prev, imageEn }))}
                    />
                </div>
                <div className="form-row">
                    <Input label="Link Nút Chính (Primary URL)" name="primaryButtonUrl" value={formData.primaryButtonUrl || ''} onChange={handleChange} placeholder="#home-solutions hoặc /san-pham-giai-phap" />
                    <Input label="Link Nút Phụ (Secondary URL)" name="secondaryButtonUrl" value={formData.secondaryButtonUrl || ''} onChange={handleChange} placeholder="/tong-quan" />
                </div>
                <div className="form-row">
                    <Input label="Thứ tự sắp xếp" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} />
                    <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />
                </div>

                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Nội dung Bản dịch (Eyebrow, Title, Lead & Buttons)</p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => (
                                    <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>
                                        {lang.name}
                                    </button>
                                ))}
                            </div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input label="Tiêu đề nhỏ trên cùng (Eyebrow)" value={t.eyebrow || ''} onChange={e => handleTranslationChange(lang.id, 'eyebrow', e.target.value)} placeholder="GIẢI PHÁP CÔNG NGHỆ - CHĂM SÓC SỨC KHỎE" />
                                        <Input label="Tiêu đề chính (Title) *" value={t.title || ''} onChange={e => handleTranslationChange(lang.id, 'title', e.target.value)} required placeholder="Healthcare Solution VIETNAM" />
                                        <Input label="Mô tả / Đoạn mở đầu (Lead)" value={t.lead || ''} onChange={e => handleTranslationChange(lang.id, 'lead', e.target.value)} placeholder="KIẾN TẠO NHỮNG GIẢI PHÁP SỐ THIẾT THỰC..." />
                                        <div className="form-row">
                                            <Input label="Tên Nút Chính" value={t.primaryButtonText || ''} onChange={e => handleTranslationChange(lang.id, 'primaryButtonText', e.target.value)} placeholder="Khám phá giải pháp" />
                                            <Input label="Tên Nút Phụ" value={t.secondaryButtonText || ''} onChange={e => handleTranslationChange(lang.id, 'secondaryButtonText', e.target.value)} placeholder="Về chúng tôi" />
                                        </div>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}
                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật' : 'Thêm mới'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/home-banners')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default HomeBannersForm;
